import React, { useMemo } from 'react';
import { Box, MenuItem, TextField } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { z } from 'zod';
import { formatDate } from '../../utils/helper';

export const isValidProspectUrl = (value, { linkedin = false } = {}) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return true;
  if (/\s/.test(trimmed)) return false;
  const withScheme = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
  let parsed;
  try {
    parsed = new URL(withScheme);
  } catch {
    return false;
  }
  const hostname = parsed.hostname.toLowerCase();
  if (!['http:', 'https:'].includes(parsed.protocol) || parsed.username || parsed.password) return false;
  if (
    !/^[a-z0-9.-]+$/.test(hostname) ||
    !hostname.includes('.') ||
    hostname.startsWith('.') ||
    hostname.endsWith('.')
  ) {
    return false;
  }
  return !linkedin || hostname === 'linkedin.com' || hostname.endsWith('.linkedin.com');
};

export const prospectFormSchema = z.object({
  client: z.string().min(1, 'Client is required'),
  program: z.string().min(1, 'Program is required'),
  company: z.string().trim().min(1, 'Prospect company is required').max(500, 'Prospect company is too long'),
  contactName: z.string().trim().min(1, 'Contact name is required').max(500, 'Contact name is too long'),
  title: z.string().max(500, 'Title is too long'),
  website: z
    .string()
    .max(2000, 'Website is too long')
    .refine((value) => isValidProspectUrl(value), 'Enter a valid web address, for example example.com'),
  linkedin: z
    .string()
    .max(2000, 'LinkedIn is too long')
    .refine(
      (value) => isValidProspectUrl(value, { linkedin: true }),
      'Enter a linkedin.com address, for example linkedin.com/in/name',
    ),
  howDoYouKnowThisPerson: z.string().max(2000, 'This field is too long'),
  whyRelevant: z.string().max(2000, 'This field is too long'),
  anythingElse: z.string().max(2000, 'This field is too long'),
});

export const emptyProspectForm = {
  client: '',
  program: '',
  company: '',
  contactName: '',
  title: '',
  website: '',
  linkedin: '',
  howDoYouKnowThisPerson: '',
  whyRelevant: '',
  anythingElse: '',
};

export const toProspectForm = (prospect = {}) => ({
  client: String(prospect.client?._id || prospect.client || ''),
  program: String(prospect.program?._id || prospect.program || ''),
  company: prospect.company || '',
  contactName: prospect.contactName || '',
  title: prospect.title || '',
  website: prospect.website || '',
  linkedin: prospect.linkedin || '',
  howDoYouKnowThisPerson: prospect.howDoYouKnowThisPerson || '',
  whyRelevant: prospect.whyRelevant || '',
  anythingElse: prospect.anythingElse || '',
});

export const prospectFormChanged = (prospect, values) => {
  const current = toProspectForm(prospect);
  return Object.keys(emptyProspectForm).some(
    (key) => String(current[key] || '').trim() !== String(values?.[key] || '').trim(),
  );
};

export const useClientProgramOptions = (companies = [], products = [], clientId) => {
  const clientOptions = useMemo(
    () =>
      (companies || [])
        .filter((item) => item?.client?._id || item?.client)
        .map((item) => ({
          value: String(item.client?._id || item.client),
          companyName: item.companyName || '',
          companyLogo: item.companyLogo,
          companyId: String(item._id),
        }))
        .sort((a, b) => a.companyName.localeCompare(b.companyName)),
    [companies],
  );

  const programsForClient = useMemo(() => {
    const company = (companies || []).find((item) => String(item?.client?._id || item?.client) === String(clientId));
    if (!company?._id) return [];
    return (products || [])
      .filter((product) => String(product.company?._id || product.company) === String(company._id))
      .sort((a, b) => String(a.productName || '').localeCompare(String(b.productName || '')));
  }, [companies, products, clientId]);

  return { clientOptions, programsForClient };
};

export const formatSubmittedDate = (value) => formatDate(value);

export const leadAgentName = (leadAgent) =>
  [leadAgent?.firstName, leadAgent?.lastName].filter(Boolean).join(' ').trim();

export const externalHref = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

export const prospectErrorMessage = (error, fallback) => error?.response?.data?.message || fallback;

export const tableHeadCellSx = { whiteSpace: 'nowrap' };

export const tableCellSx = { whiteSpace: 'nowrap' };

export const ProspectTextField = ({ name, label, placeholder, multiline, minRows, ...rest }) => {
  const {
    register,
    formState: { errors },
  } = useFormContext();

  return (
    <TextField
      name={name}
      label={label}
      placeholder={placeholder}
      fullWidth
      size='small'
      multiline={multiline}
      minRows={minRows}
      {...register(name)}
      error={Boolean(errors?.[name])}
      helperText={errors?.[name]?.message || ''}
      {...rest}
    />
  );
};

export const ProspectSelect = ({ name, label, placeholder, options, disabled, required }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field }) => (
        <TextField
          {...field}
          select
          label={label}
          required={required}
          fullWidth
          size='small'
          disabled={disabled}
          error={Boolean(errors?.[name])}
          helperText={errors?.[name]?.message || ''}
          SelectProps={{
            displayEmpty: true,
            renderValue: (selected) => {
              const match = options.find((option) => option.value === selected);
              if (!selected || !match) {
                return (
                  <Box component='span' sx={{ color: '#94a3b8' }}>
                    {placeholder || 'Select'}
                  </Box>
                );
              }
              return match.label;
            },
          }}
        >
          <MenuItem value='' sx={{ display: placeholder ? 'none' : undefined }}>
            {placeholder || 'Select'}
          </MenuItem>
          {options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
      )}
    />
  );
};

export const FormRow = ({ label, children, align = 'center' }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: '200px 1fr' },
      columnGap: 2,
      rowGap: 0.5,
      alignItems: align,
      mb: 1.75,
    }}
  >
    <Box component='label' sx={{ fontSize: 14, fontWeight: 600, color: '#334155', pt: align === 'flex-start' ? 1 : 0 }}>
      {label}
    </Box>
    <Box>{children}</Box>
  </Box>
);
